/**
 * Everything a seat holds, laid out on the table in front of its mat: points
 * and votes, the supply and both camps, bonus tiles, forum tiles, kept Trajan
 * demand tiles, extra-action tiles, buildings, the hand and the display.
 *
 * A hand is face up only for its owner — the view carries no one else's cards,
 * so another seat's hand is drawn as backs, as it would be across a table.
 */
import { senateVotes, type PlayerView } from '@trajan/engine';
import { Container, FillGradient, Graphics } from 'pixi.js';
import {
  BONUS_ROW,
  BUILT_ROW,
  CARD_H,
  CARD_W,
  DISPLAY,
  EXTRA_ROW,
  HAND,
  HELD_TILE,
  LEGION_CAMP,
  SUPPLY,
  TILES_ROW,
  VP_BADGE,
  WORKER_CAMP,
  bonusTiles,
  builtStacks,
  displayCard,
  extraTiles,
  handCard,
  heldTiles,
  type Rect,
} from '../layout';
import { BRONZE, BRONZE_DARK, BRONZE_LIGHT, INK, PICKED, seatPaint } from '../palette';
import type { Register } from './board';
import { icon } from './icons';
import { cardBack, label, seatToken } from './pieces';
import {
  bonusChip,
  buildingFace,
  commodityCard,
  extraTileFace,
  forumTileFace,
  trajanFace,
} from './tiles';

const CAPTION = 0xe9dcc0;

const at = <T extends Container>(parent: Container, child: T, x: number, y: number): T => {
  child.position.set(x, y);
  parent.addChild(child);
  return child;
};

function caption(
  parent: Container,
  text: string,
  x: number,
  y: number,
  anchor: [number, number] = [0, 0.5],
) {
  const t = at(
    parent,
    label(text.toUpperCase(), 10.5, { face: 'display', fill: CAPTION, anchor, spacing: 1.2 }),
    x,
    y,
  );
  t.alpha = 0.78;
}

/** A shallow felt-lined box, for the supply and the camps. */
function well(r: Rect): Graphics {
  const g = new Graphics();
  g.roundRect(r.x, r.y, r.w, r.h, 10).fill({ color: 0x000000, alpha: 0.28 });
  g.roundRect(r.x + 1, r.y + 2, r.w - 2, r.h - 2, 9).stroke({
    width: 1.5,
    color: 0x000000,
    alpha: 0.25,
  });
  g.roundRect(r.x, r.y, r.w, r.h, 10).stroke({ width: 1, color: 0xe9dcc0, alpha: 0.18 });
  return g;
}

/** Up to `shown` tokens scattered in a well, and the count beside them. */
function tokens(parent: Container, seat: number, count: number, r: Rect, title: string) {
  parent.addChild(well(r));
  caption(parent, title, r.x + 8, r.y + 11);
  const shown = Math.min(count, 6);
  for (let k = 0; k < shown; k++) {
    const col = k % 3;
    const row = Math.floor(k / 3);
    at(parent, seatToken(seat, 6), r.x + 15 + col * 13 + row * 6, r.y + 33 + row * 13);
  }
  at(
    parent,
    label(String(count), 19, { face: 'display', fill: 0xfff1d0, anchor: [1, 0.5] }),
    r.x + r.w - 8,
    r.y + r.h / 2 + 8,
  );
}

export interface TableauOptions {
  /** Cards lifted out of the hand: chosen for a set, or for the discard. */
  raised?: ReadonlySet<string>;
  /** What to call this seat — "your", or a name's possessive — for the corner caption. */
  owner?: string;
}

export function tableau(p: PlayerView, register: Register, opts: TableauOptions = {}): Container {
  const root = new Container();
  const seat = p.id;

  // points, and votes at the balance of power
  const vp = at(root, new Container(), VP_BADGE.x, VP_BADGE.y);
  register('vp', vp);
  const cx = VP_BADGE.w / 2;
  const disc = new Graphics();
  disc.circle(cx + 1, cx + 3, 34).fill({ color: 0x000000, alpha: 0.35 });
  disc.circle(cx, cx, 34).fill(
    new FillGradient({
      type: 'radial',
      center: { x: 0.35, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.6,
      colorStops: [
        { offset: 0, color: BRONZE_LIGHT },
        { offset: 0.6, color: BRONZE },
        { offset: 1, color: BRONZE_DARK },
      ],
      textureSpace: 'local',
    }),
  );
  disc.circle(cx, cx, 27).fill(0xf6ecd4);
  vp.addChild(disc);
  at(vp, icon('laurel', 50, 0x8c7a4a), cx, cx + 2);
  at(vp, label(String(p.vp), 22, { face: 'display', fill: INK }), cx, cx - 2);
  at(vp, label('VP', 8.5, { face: 'display', fill: BRONZE_DARK }), cx, cx + 15);
  at(
    root,
    label(`${senateVotes(p)} votes`, 11.5, { fill: CAPTION, weight: '700' }),
    VP_BADGE.x + cx,
    VP_BADGE.y + VP_BADGE.h + 9,
  ).alpha = 0.85;

  const supply = at(root, new Container(), 0, 0);
  register('supply', supply);
  tokens(supply, seat, p.supply, SUPPLY, 'supply');
  const legion = at(root, new Container(), 0, 0);
  register('camp:legion', legion);
  tokens(legion, seat, p.legionnaires, LEGION_CAMP, 'legion camp');
  const workers = at(root, new Container(), 0, 0);
  register('camp:workers', workers);
  tokens(workers, seat, p.workers, WORKER_CAMP, "workers' camp");

  // bonus tiles, each at the rate its side pays
  caption(root, 'bonus tiles', BONUS_ROW.x, BONUS_ROW.y - 2);
  for (const b of bonusTiles(p)) {
    const t = p.bonusTiles.find((x) => x.id === b.id);
    if (!t) continue;
    const c = at(root, bonusChip(t, t.side, b.rect.w), b.rect.x, b.rect.y);
    register(`held:${b.id}`, c);
    register.piece(b.id, c);
  }

  // forum tiles and kept Trajan demand tiles
  caption(root, 'forum tiles', TILES_ROW.x, TILES_ROW.y - 12);
  for (const h of heldTiles(p)) {
    const forum = p.forumTiles.find((t) => t.id === h.id);
    const trajan = p.trajanDemandTiles.find((t) => t.id === h.id);
    const face = forum
      ? forumTileFace(forum, HELD_TILE)
      : trajan
        ? trajanFace(trajan, HELD_TILE)
        : null;
    if (!face) continue;
    const c = at(root, face, h.rect.x, h.rect.y);
    register(`held:${h.id}`, c);
    register.piece(h.id, c);
  }
  if (p.forumTiles.length + p.trajanDemandTiles.length === 0) empty(root, TILES_ROW, 'none yet');

  caption(root, 'extra actions', EXTRA_ROW.x, EXTRA_ROW.y - 12);
  for (const h of extraTiles(p)) {
    const t = p.extraActionTiles.find((x) => x.id === h.id);
    if (!t) continue;
    const c = at(root, extraTileFace(t, HELD_TILE), h.rect.x, h.rect.y);
    register(`held:${h.id}`, c);
    register.piece(h.id, c);
  }
  if (p.extraActionTiles.length === 0) empty(root, EXTRA_ROW, 'none held');

  caption(root, 'buildings', BUILT_ROW.x, BUILT_ROW.y - 12);
  for (const g of builtStacks(p)) {
    const tiles = p.constructionTiles.filter((t) => g.ids.includes(t.id));
    const top = tiles[tiles.length - 1];
    if (!top) continue;
    const c = at(root, new Container(), g.rect.x, g.rect.y);
    register(`held:${g.ids[0]!}`, c);
    register.piece(top.id, c);
    for (let k = Math.min(2, tiles.length - 1); k >= 1; k--) {
      at(c, buildingFace(top, HELD_TILE, HELD_TILE), k * 2, k * 2);
    }
    at(c, buildingFace(top, HELD_TILE, HELD_TILE), 0, 0);
    if (tiles.length > 1) {
      const badge = new Graphics()
        .circle(HELD_TILE - 2, HELD_TILE - 2, 9)
        .fill(0x3a2412)
        .stroke({ width: 1, color: BRONZE_LIGHT });
      c.addChild(badge);
      at(
        c,
        label(`${tiles.length}`, 11, { face: 'display', fill: 0xfff1d0 }),
        HELD_TILE - 2,
        HELD_TILE - 1.5,
      );
    }
  }
  if (p.constructionTiles.length === 0) empty(root, BUILT_ROW, 'nothing built');

  // the hand: face up to its owner, backs to everyone else
  const hand = p.hand;
  caption(root, hand ? `hand · ${hand.length}` : `hand · ${p.handCount}`, HAND.x, HAND.y);
  if (hand) {
    hand.forEach((card, k) => {
      const r = handCard(k, hand.length);
      const up = opts.raised?.has(card.id) ?? false;
      const c = at(root, new Container(), r.x, r.y - (up ? 12 : 0));
      c.addChild(commodityCard(card, CARD_W, CARD_H));
      if (up) {
        const ring = new Graphics()
          .roundRect(-3, -3, CARD_W + 6, CARD_H + 6, 7)
          .stroke({ width: 2.5, color: PICKED });
        c.addChild(ring);
      }
      register(`card:${card.id}`, c);
      register.piece(card.id, c);
    });
    if (hand.length === 0) empty(root, { ...HAND, y: HAND.y + 12, h: CARD_H }, 'no cards');
  } else {
    for (let k = 0; k < p.handCount; k++) {
      const r = handCard(k, p.handCount);
      at(root, cardBack(CARD_W, CARD_H), r.x, r.y);
    }
  }

  const display = at(root, new Container(), 0, 0);
  register('display', display);
  caption(display, `display · ${p.display.length}`, DISPLAY.x + 6, DISPLAY.y);
  p.display.forEach((card, k) => {
    const r = displayCard(k, p.display.length);
    const c = at(display, commodityCard(card, CARD_W, CARD_H), r.x, r.y);
    register(`card:${card.id}`, c);
    register.piece(card.id, c);
  });
  if (p.display.length === 0) {
    const g = new Graphics().roundRect(DISPLAY.x + 6, DISPLAY.y + 12, DISPLAY.w - 12, CARD_H, 8);
    g.stroke({ width: 1.2, color: CAPTION, alpha: 0.25 });
    display.addChild(g);
    at(
      display,
      label('cards put on display\nscore at the end', 10, { fill: CAPTION }),
      DISPLAY.x + DISPLAY.w / 2,
      DISPLAY.y + 12 + CARD_H / 2,
    ).alpha = 0.6;
  }

  if (opts.owner) {
    const paint = seatPaint(seat);
    const x = DISPLAY.x + DISPLAY.w - 6;
    const y = DISPLAY.y + DISPLAY.h + 16;
    const tag = new Graphics()
      .circle(x, y, 5)
      .fill(paint.base)
      .stroke({ width: 1, color: paint.dark });
    root.addChild(tag);
    caption(root, opts.owner, x - 10, y, [1, 0.5]);
  }
  return root;
}

function empty(parent: Container, r: Rect, text: string) {
  const t = at(
    parent,
    label(text, 12, { fill: CAPTION, anchor: [0, 0.5] }),
    r.x + 2,
    r.y + r.h / 2,
  );
  t.alpha = 0.5;
}
